import React, { useMemo, useState } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  InputAdornment, Link as MUILink, Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Link } from "react-router-dom";
import { getMessage, useLocale } from "../i18n/locale";
import { authService } from "../services/resources.service";
import { storeSessionToken } from "../services/authSession";
import AppVersion from "./AppVersion";

export default function LoginModal({ open, onClose, onSignedIn, reasonKey = "loginModalDefaultReason" }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { locale } = useLocale();
  const message = (key) => getMessage(locale, key);
  const canSubmit = useMemo(
    () => email.trim().length > 5 && password.length >= 4,
    [email, password]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    setError("");
    try {
      const result = await authService.signIn({ email: email.trim(), password });
      storeSessionToken(result.token);
      onSignedIn?.(result.user);
      setPassword("");
    } catch (requestError) {
      setError(requestError?.message || message("authSignInFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError("");
    setPassword("");
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="login-modal-title"
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, sm: 4 },
          m: { xs: 2, sm: 4 },
          width: { xs: "calc(100% - 32px)", sm: "100%" },
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        id="login-modal-title"
        sx={{ px: { xs: 2.5, sm: 3 }, pt: { xs: 2.5, sm: 3 }, pb: 1, pr: 6, fontWeight: 900 }}
      >
        {message("loginModalTitle")}
        <IconButton
          aria-label={message("close")}
          onClick={handleClose}
          disabled={loading}
          sx={{ position: "absolute", insetInlineEnd: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2.5, sm: 3 } }}>
        <Stack component="form" id="login-modal-form" onSubmit={handleSubmit} spacing={2} sx={{ pt: 0.5 }}>
          <Typography color="text.secondary">{message(reasonKey)}</Typography>
          <TextField
            label={message("authEmail")}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoFocus
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><EmailIcon /></InputAdornment>
              ),
            }}
          />
          <TextField
            label={message("authPassword")}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={message("authTogglePassword")}
                    onClick={() => setShowPassword((visible) => !visible)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={{ xs: 1, sm: 2 }}
          >
            <MUILink component={Link} to="/auth/reset" onClick={handleClose} underline="hover">
              {message("authForgotPassword")}
            </MUILink>
            <Typography variant="body2" color="text.secondary">
              {message("authNewHere")}{" "}
              <MUILink component={Link} to="/auth/signup" onClick={handleClose} underline="hover">
                {message("authCreateAccount")}
              </MUILink>
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, pb: { xs: 2.5, sm: 3 }, pt: 1 }}>
        <Stack spacing={1} sx={{ width: "100%" }} alignItems="center">
          <Button
            type="submit"
            form="login-modal-form"
            variant="contained"
            size="large"
            disabled={!canSubmit || loading}
            fullWidth
          >
            {message(loading ? "authSigningIn" : "authSignIn")}
          </Button>
          <AppVersion />
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
